import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useLocation, matchPath } from 'react-router-dom';

// material-ui
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';

// ant design icons
import { DownOutlined, UpOutlined } from '@ant-design/icons';

// project imports
import NavItem from './NavItem';
import { useGetMenuMaster } from 'api/menu';

export default function NavCollapse({ item, level = 1 }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { pathname } = useLocation();

  // Check if any child item matches current location
  const isChildSelected = item.children?.some((child) =>
    matchPath({ path: child.url || child.link || '', end: true }, pathname)
  );

  const [open, setOpen] = useState(Boolean(isChildSelected));

  useEffect(() => {
    if (isChildSelected) {
      setOpen(true);
    }
  }, [pathname, isChildSelected]);

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} />
  ) : null;

  const textColor = 'text.primary';
  const iconSelectedColor = 'primary.main';

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        selected={isChildSelected}
        sx={{
          zIndex: 1201,
          pl: drawerOpen ? `${level * 28}px` : 1.5,
          py: !drawerOpen && level === 1 ? 1.25 : 1,
          '&:hover': { bgcolor: 'primary.lighter' },
          ...(isChildSelected && {
            color: iconSelectedColor,
            '&:hover': { color: iconSelectedColor, bgcolor: 'primary.lighter' }
          }),
          ...(!drawerOpen && {
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
          })
        }}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: isChildSelected ? iconSelectedColor : textColor,
              ...(!drawerOpen && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { bgcolor: 'secondary.lighter' }
              }),
              ...(!drawerOpen &&
                isChildSelected && {
                  bgcolor: 'primary.lighter',
                  '&:hover': { bgcolor: 'primary.lighter' }
                })
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {drawerOpen && (
          <ListItemText
            primary={
              <Typography
                variant="h6"
                sx={{
                  color: isChildSelected ? iconSelectedColor : textColor,
                  fontWeight: isChildSelected ? 600 : 400
                }}
              >
                {item.title}
              </Typography>
            }
          />
        )}
        {drawerOpen && (
          open ? (
            <UpOutlined style={{ fontSize: '0.65rem', marginLeft: 'auto', color: isChildSelected ? 'inherit' : '#8c8c8c' }} />
          ) : (
            <DownOutlined style={{ fontSize: '0.65rem', marginLeft: 'auto', color: isChildSelected ? 'inherit' : '#8c8c8c' }} />
          )
        )}
      </ListItemButton>

      {drawerOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ position: 'relative' }}>
            {item.children?.map((child) => (
              <NavItem key={child.id} item={child} level={level + 1} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

NavCollapse.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};
